import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef,
         HostListener, Inject, OnDestroy, OnInit, ViewChild
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { GalleryService } from 'app/gallery/gallery.service';
import { ProductConfig } from 'app/product-designer-config';
import { Progress, ProgressService } from 'app/progress/progress.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { AdditionalInfoItemConfig, AdditionalInfoItemConfigPosition, StepConfig,
         TemplateConfig, TemplateGroupConfig, TemplatesSettings, PartConfig
       } from 'collage-designer/collage-designer-config';
import { PlaceholderType
       } from 'collage-designer/stage/placeholder/placeholder';
import { StageComponent } from 'collage-designer/stage/stage.component';
import { StageService } from 'collage-designer/stage/stage.service';
import { PriceCalculationService } from 'core/price/price-calculation.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { DesignStepService } from './design-step.service';

@Component({
    selector: 'design-step',
    templateUrl: './design-step.component.html',
    styleUrls: ['./design-step.component.scss'],
    standalone: false
})
export class DesignStepComponent implements OnInit, OnDestroy, AfterViewChecked
{
  private static readonly ICONS: Array<string> =
      ['upload', 'photos', 'layouts', 'text'];
  private static readonly PROGRESS_CHANGEABLE_CHECK_COOLDOWN: number = 200;
                                                                          // ms

  public toolboxes: Array<string> = [];
  public displayProductChooser: boolean = false;
  public isSmallDevice: boolean = false;
  public isMediumDevice: boolean = false;
  public isLandscape: boolean = false;
  public activeToolbox: string;
  public additionalInfoItems: Array<AdditionalInfoItemConfig>;
  public showCreateNewTextPlaceholdersButton: boolean = false;
  public productConfig: ProductConfig;
  public nextProgress: Progress;
  public progressChangeable: boolean = true;

  public toolboxTabs: Array<string> = [];
  public availableToolboxes: Array<string> = [];
  public selectedToolboxTabsIndex: number = 0;
  public templateGroups: Array<TemplateGroupConfig> = [];
  public templatesSettings: TemplatesSettings = undefined;

  @ViewChild('productChooserToolbox') public productChooserToolbox: any;
  @ViewChild('imageSelect') public imageSelect: ElementRef;
  @ViewChild('toolboxTriggers') public toolboxTriggers: ElementRef;
  @ViewChild('stage') public stageComponent: StageComponent;

  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;
  private partChangedSubscription:
      AsyncEventEmitterSubscriber<PartConfig>;
  private uploadProgressChangedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private subscriptions: Subscription = new Subscription();

  public constructor(
      private cdr: ChangeDetectorRef,
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(ToolboxService) private toolboxService: ToolboxService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(PriceCalculationService) public priceCalculationService,
      @Inject(ProgressService) private progressService,
      @Inject(StageService) private stageService,
  )
  {
    this.loadIcons();
    this.productConfig = this.productService.getCurrentProduct();

    const displayProductChooser = () =>
        (this.productService.getProducts().length > 1) &&
        this.designStepService.getDesignStepId() === 0;

    this.displayProductChooser = displayProductChooser();
    this.additionalInfoItems =
        this.designStepService.getDesignStepConfig().additionalInfoItems;
    this.showCreateNewTextPlaceholdersButton = this.designStepService
      .getDesignStepConfig().textPlaceholdersSettings.showCreateNewButton;
    this.updateProgressState();

    this.subscriptions.add(
      this.designStepService.designStepChanged.subscribe(
            (currentStepConfig: StepConfig) => {
              this.displayProductChooser = displayProductChooser();
              this.additionalInfoItems = currentStepConfig.additionalInfoItems;
              this.showCreateNewTextPlaceholdersButton = currentStepConfig
                .textPlaceholdersSettings.showCreateNewButton;
            }
      ));

    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(
          this.updateAvailableToolboxes.bind(this));

    this.isSmallDevice = Utils.isSmallDevice();
    this.isMediumDevice = Utils.isMediumDevice();
    this.isLandscape = Utils.isLandscape() &&
        window.innerWidth / window.innerHeight > 6 / 5;

    this.subscriptions.add(
        this.toolboxService.targetChanged.subscribe((activeToolbox) => {
          this.activeToolbox = undefined;
          this.cdr.detectChanges();
          this.activeToolbox = this.toolboxService.getActiveToolboxName();

          this.updateSelectedTabbedToolboxIndex(this.activeToolbox);
        }));

    this.partChangedSubscription =
        this.designStepService.partChanged.subscribe(
          this.updateAvailableTemplates.bind(this));
    this.subscriptions.add(
        this.progressService.progressChanged.subscribe((progress) => {
          this.updateProgressState();
        }));

    // We shouldn't wait for updateProgressChangeable, because it's blocking.
    // If we wait, the state of `getUploadInProgress` may never be set
    // (e.g., in the case of `Memo`).
    this.uploadProgressChangedSubscription =
        this.galleryService.uploadProgressChanged.subscribe(
            async () => { void this.updateProgressChangeable(); });
    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(
            async () => { void this.updateProgressChangeable(); });
  }

  private async updateProgressChangeable(): Promise<void>
  {
    this.progressChangeable = !this.galleryService.getUploadInProgress();

    while (!this.progressChangeable) {
      if (this.stageComponent.hasEmptyImagePlaceholders()) {
        this.progressChangeable = true;
        break;
      }
      await Utils.sleep(DesignStepComponent.PROGRESS_CHANGEABLE_CHECK_COOLDOWN);
    }
  }

  private updateProgressState(): void
  {
    this.nextProgress = this.progressService.getNextProgress();
  }

  public async ngOnInit(): Promise<void>
  {
    /**
     * Bei Klick auf die Seitenzahlen entfernt Angular entsprechende Elemente
     * aus dem DOM. Damit trotzdem geprüft werden kann, ob innerhalb einer
     * statischen Toolbox geklickt wurde, muss bereits auf ein mousedown-Event
     * gelauscht werden.
     */
    (<any>document).addEventListener(
        'touchstart', this.closeStaticToolboxes, { passive: true });
    (<any>document).addEventListener(
        'mousedown', this.closeStaticToolboxes, { passive: true });
    await this.updateAvailableToolboxes();
    await this.updateAvailableTemplates();
  }

  public ngAfterViewChecked(): void
  {
    if (this.toolboxTriggers &&
        this.toolboxTriggers.nativeElement.childElementCount % 4 === 0) {
      this.toolboxTriggers.nativeElement.classList.add('marketing-two-per-row');
    }
  }

  private loadIcons(): void
  {
    for (const icon of DesignStepComponent.ICONS) {
      this.mir.addSvgIcon(
          icon,
          this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
              `assets/img/icons/${icon}.svg`)));
    }
  }

  private async updateAvailableTemplates(): Promise<void>
  {
    this.templateGroups = await this.designStepService.getTemplateGroups();
    this.templatesSettings = this.designStepService.getTemplatesSettings();
  }

  private async updateAvailableToolboxes(): Promise<void>
  {
    const allowedToolboxes = ['LayoutToolbox', 'GalleryToolbox'];

    this.availableToolboxes = await this.designStepService.getToolboxesNames();

    this.toolboxTabs = allowedToolboxes
      .filter(tbName => this.availableToolboxes.indexOf(tbName) !== -1);
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);
    this.designStepService.partChanged.unsubscribe(
        this.partChangedSubscription);
    this.galleryService.uploadProgressChanged.unsubscribe(
        this.uploadProgressChangedSubscription);
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);

    document.removeEventListener('touchstart', this.closeStaticToolboxes);
    document.removeEventListener('mousedown', this.closeStaticToolboxes);
  }

  private closeStaticToolboxes(event): void
  {
    event.stopImmediatePropagation();

    const toolboxElements = Array.from(
        document.querySelectorAll('.toolbox.static > div'));
    const clickedAtStaticToolbox = toolboxElements
      .some(element => element.contains(event.target));

    if (!clickedAtStaticToolbox) {
      toolboxElements.map((e) => {
        let element = <HTMLElement>e;
        if (element.classList.contains('icon')
            && element.classList.contains('hide')) {
          element.classList.remove('hide');
        }
        if (element.classList.contains('content')) {
          element.classList.add('hide');
        }
      });
    }
  }

  public get currentProductName(): string|undefined
  {
    return this.productService.getCurrentProduct().name;
  }

  public get priceName(): string|undefined
  {
    // If we display a product name, then we prefer to display "price" as label
    // instead of a short product name.
    if (this.currentProductName) {
      return undefined;
    }
    return this.productService.getCurrentProduct().shortName;
  }

  public photosButtonClicked(event: Event): void
  {
    const shouldUploadPhotoImmediately =
      (this.galleryService.getImages().length === 0 &&
       !this.galleryService.getUploadInProgress()) ||
      (!Utils.isSmallDevice() && !Utils.isMediumDevice());

    if (shouldUploadPhotoImmediately) {
      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'init_photo_upload',
        label: 'photos_button',
      });

      this.imageSelect.nativeElement.click();
    }
    this.toolboxService.open('GalleryToolbox');
  }

  public createNewTextPlaceholdersButtonClicked(event: Event): void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'enter_text',
      label: 'start'
    });
    this.designStepService.createNewPlaceholder(PlaceholderType.Text);
  }

  public isStaticToolboxAvailable(toolboxName: string): boolean
  {
    return this.availableToolboxes.indexOf(toolboxName) !== -1;
  }

  public updateSelectedTabbedToolboxIndex(toolboxName: string): void
  {
    const tabIdx = this.toolboxTabs.indexOf(toolboxName);
    if (tabIdx > -1) {
      this.selectedToolboxTabsIndex = tabIdx;
    }
  }

  // To ensure that window.innerWidth is correctly updated for device checks,
  // especially in Chrome because it fires resize events before final rendering,
  // we call requestAnimationFrame twice and listen to both events
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  public onResize(): void
  {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isSmallDevice = Utils.isSmallDevice();
        this.isMediumDevice = Utils.isMediumDevice();
        this.isLandscape = Utils.isLandscape() &&
            window.innerWidth / window.innerHeight > 6 / 5;
      });
    });
  }

  public get additionalInfoItemsDesignArea(): Array<AdditionalInfoItemConfig>
  {
    return this.additionalInfoItems.filter(
        aii => aii.position === AdditionalInfoItemConfigPosition.designArea);
  }

  public get additionalInfoItemsToolboxes(): Array<AdditionalInfoItemConfig>
  {
    return this.additionalInfoItems.filter(
        aii => aii.position === AdditionalInfoItemConfigPosition.toolboxes);
  }
}
